import { matchPath } from 'react-router'
import { type AdminPageView, type RecentlyViewedEntry } from './recentlyViewed.types'

const ADMIN_PATH_PREFIX = '/app/admin'

// True for the admin landing page and anything nested beneath it. Used both to
// gate which views we record and which entries we surface — the list only ever
// shows pages under `/app/admin`.
export const isAdminPath = (pathname: string): boolean =>
  pathname === ADMIN_PATH_PREFIX || pathname.startsWith(`${ADMIN_PATH_PREFIX}/`)

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const NUMERIC_RE = /^\d+$/
// Opaque token: reasonably long and mixes letters with digits (e.g. base64url
// or hashed ids). Slug-like segments (all letters, hyphenated words) are left
// alone so real route names survive normalization.
const OPAQUE_TOKEN_RE = /^(?=.*[a-z])(?=.*\d)[a-z0-9_-]{12,}$/i

const isDynamicSegment = (segment: string): boolean =>
  NUMERIC_RE.test(segment) || UUID_RE.test(segment) || OPAQUE_TOKEN_RE.test(segment)

// Collapse id-like path segments to `:id` so repeat visits to the same page
// with different entity ids aggregate together. Used as the normalization key
// when a path matches no registered route pattern.
export const collapseDynamicSegments = (pathname: string): string => {
  const trimmed = pathname.replace(/\/+$/, '') || '/'
  return trimmed
    .split('/')
    .map(seg => (isDynamicSegment(seg) ? ':id' : seg))
    .join('/')
}

const countLiteralSegments = (pattern: string): number =>
  pattern.split('/').filter(seg => seg && !seg.startsWith(':')).length

// Resolve a concrete pathname to the most-specific registered route pattern.
// The route pattern is a stable key across different entity ids, giving us
// route-pattern normalization for free (e.g. `/sites/1` and `/sites/2` both map
// to `/sites/:storeConfigId`). Falls back to collapsing id-like segments when
// the path matches no registered route. When several patterns match, the one
// with the most literal segments wins — mirroring how react-router itself picks
// a winner among overlapping routes.
export const resolveRoutePattern = (
  pathname: string,
  routesByName: Record<string, string>
): { key: string; routeName?: string } => {
  let best: { key: string; routeName: string; specificity: number } | undefined
  for (const [routeName, pattern] of Object.entries(routesByName)) {
    if (!matchPath(pathname, { path: pattern, exact: true })) continue
    const specificity = countLiteralSegments(pattern)
    if (!best || specificity > best.specificity) {
      best = { key: pattern, routeName, specificity }
    }
  }
  if (best) return { key: best.key, routeName: best.routeName }
  return { key: collapseDynamicSegments(pathname) }
}

// Aggregate raw view events into a deduped, windowed, sorted list. Events older
// than `windowMs` and any `excludeKeys` (e.g. the admin home itself) are
// dropped. Each surviving key keeps its visit count and the concrete path /
// route of its most recent visit. Sorted by view count desc, breaking ties by
// recency for stable ordering.
export const aggregateRecentlyViewed = (
  events: AdminPageView[],
  now: number,
  windowMs: number,
  limit: number,
  excludeKeys: ReadonlySet<string> = new Set<string>()
): RecentlyViewedEntry[] => {
  const byKey = new Map<string, RecentlyViewedEntry>()

  for (const event of events) {
    if (now - event.viewedAt > windowMs) continue
    if (excludeKeys.has(event.key)) continue

    const existing = byKey.get(event.key)
    if (!existing) {
      byKey.set(event.key, {
        key: event.key,
        path: event.path,
        routeName: event.routeName,
        count: 1,
        lastViewedAt: event.viewedAt,
      })
      continue
    }

    existing.count += 1
    if (event.viewedAt > existing.lastViewedAt) {
      existing.lastViewedAt = event.viewedAt
      existing.path = event.path
      existing.routeName = event.routeName
    }
  }

  return [...byKey.values()]
    .sort((a, b) => b.count - a.count || b.lastViewedAt - a.lastViewedAt)
    .slice(0, limit)
}

// De-slug the last meaningful segment of a path/pattern into spaced words for a
// human-readable label when no nav label exists. e.g.
// `/app/admin/onboarding/site-overview` -> `site overview`. The caller applies
// sentence casing so fallbacks read the same as nav-derived labels.
export const prettifyPath = (pathOrPattern: string): string => {
  const segments = pathOrPattern.split('/').filter(seg => seg && !seg.startsWith(':'))
  const last = segments[segments.length - 1] ?? ''
  const words = last.replace(/[-_]+/g, ' ').trim()
  return words || pathOrPattern
}
